/**
 * Streetwear Theme Interactive Logic
 * Handles 3D card deck coverflow, in-card dot image slider, quick-add modal, and drawer menus.
 */

document.addEventListener('DOMContentLoaded', () => {
  initHeroSliders();
  initCardImageSliders();
  init3DCardDecks();
  initQuickAddModal();
  initNavDrawer();
});

// ---------------------------------------------------------------------------
// 1. Hero Banner Slider
// ---------------------------------------------------------------------------
function initHeroSliders() {
  const sliders = document.querySelectorAll('.sw-hero-slider');
  sliders.forEach(slider => {
    const slides = slider.querySelectorAll('.sw-hero-slide');
    const dots = slider.querySelectorAll('.sw-hero-dot');
    if (slides.length <= 1) return;

    let currentIndex = 0;
    let timer = null;

    function goToSlide(index) {
      slides[currentIndex]?.classList.remove('active');
      dots[currentIndex]?.classList.remove('active');
      currentIndex = (index + slides.length) % slides.length;
      slides[currentIndex]?.classList.add('active');
      dots[currentIndex]?.classList.add('active');
    }

    dots.forEach((dot, idx) => {
      dot.addEventListener('click', () => {
        goToSlide(idx);
        resetTimer();
      });
    });

    function startTimer() {
      timer = setInterval(() => {
        goToSlide(currentIndex + 1);
      }, 5500);
    }

    function resetTimer() {
      if (timer) clearInterval(timer);
      startTimer();
    }

    startTimer();
  });
}

// ---------------------------------------------------------------------------
// 2. In-Card Multi-Image Dot Slider
// ---------------------------------------------------------------------------
function initCardImageSliders() {
  const cards = document.querySelectorAll('.sw-product-card');
  cards.forEach(card => {
    const track = card.querySelector('.sw-card-slider-track');
    const dots = card.querySelectorAll('.sw-card-dot');
    const slides = card.querySelectorAll('.sw-card-slide-img');
    if (!track || slides.length <= 1) return;

    dots.forEach((dot, idx) => {
      dot.addEventListener('mouseenter', () => switchImage(idx));
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        switchImage(idx);
      });
    });

    // Touch swipe support on card
    let startX = 0;
    let currentIdx = 0;
    track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    track.addEventListener('touchend', (e) => {
      const endX = e.changedTouches[0].clientX;
      const diff = startX - endX;
      if (Math.abs(diff) > 40) {
        if (diff > 0 && currentIdx < slides.length - 1) {
          currentIdx++;
        } else if (diff < 0 && currentIdx > 0) {
          currentIdx--;
        }
        switchImage(currentIdx);
      }
    }, { passive: true });

    function switchImage(index) {
      currentIdx = index;
      track.style.transform = `translateX(-${index * 100}%)`;
      dots.forEach((d, i) => d.classList.toggle('active', i === index));
    }
  });
}

// ---------------------------------------------------------------------------
// 3. 3D Stacked Card Deck (Bluorng Coverflow)
// ---------------------------------------------------------------------------
function init3DCardDecks() {
  const stages = document.querySelectorAll('.sw-deck-stage');
  stages.forEach(stage => {
    const cards = Array.from(stage.querySelectorAll('.sw-deck-card'));
    if (!cards.length) return;

    let activeIndex = Math.floor(cards.length / 2);

    function updateDeck() {
      cards.forEach((card, index) => {
        const offset = index - activeIndex;
        const absOffset = Math.abs(offset);

        if (offset === 0) {
          // Center / Front card
          card.style.transform = 'translate3d(0, 0, 0) scale(1)';
          card.style.zIndex = 10;
          card.style.opacity = '1';
          card.style.pointerEvents = 'auto';
        } else if (offset === -1) {
          // Left card
          card.style.transform = 'translate3d(-28%, 0, -80px) rotateY(12deg) scale(0.9)';
          card.style.zIndex = 6;
          card.style.opacity = '0.75';
          card.style.pointerEvents = 'auto';
        } else if (offset === 1) {
          // Right card
          card.style.transform = 'translate3d(28%, 0, -80px) rotateY(-12deg) scale(0.9)';
          card.style.zIndex = 6;
          card.style.opacity = '0.75';
          card.style.pointerEvents = 'auto';
        } else if (offset < -1) {
          // Far left
          card.style.transform = `translate3d(-${28 + absOffset * 15}%, 0, -${absOffset * 80}px) scale(${1 - absOffset * 0.1})`;
          card.style.zIndex = 10 - absOffset;
          card.style.opacity = absOffset > 2 ? '0' : '0.35';
          card.style.pointerEvents = 'none';
        } else {
          // Far right
          card.style.transform = `translate3d(${28 + absOffset * 15}%, 0, -${absOffset * 80}px) scale(${1 - absOffset * 0.1})`;
          card.style.zIndex = 10 - absOffset;
          card.style.opacity = absOffset > 2 ? '0' : '0.35';
          card.style.pointerEvents = 'none';
        }
      });
    }

    cards.forEach((card, idx) => {
      card.addEventListener('click', () => {
        activeIndex = idx;
        updateDeck();
      });
    });

    // Touch swipe support
    let startX = 0;
    stage.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    stage.addEventListener('touchend', (e) => {
      const endX = e.changedTouches[0].clientX;
      const diff = startX - endX;
      if (Math.abs(diff) > 40) {
        if (diff > 0 && activeIndex < cards.length - 1) {
          activeIndex++;
        } else if (diff < 0 && activeIndex > 0) {
          activeIndex--;
        }
        updateDeck();
      }
    }, { passive: true });

    updateDeck();
  });
}

// ---------------------------------------------------------------------------
// 4. Quick Add Bottom Sheet Modal
// ---------------------------------------------------------------------------
function initQuickAddModal() {
  const modalBackdrop = document.querySelector('.sw-quick-modal-backdrop');
  if (!modalBackdrop) return;

  const modal = modalBackdrop.querySelector('.sw-quick-modal');
  const titleEl = modal.querySelector('.sw-quick-title');
  const priceEl = modal.querySelector('.sw-quick-price');
  const pillsContainer = modal.querySelector('.sw-size-pill-grid');
  const btnAdd = modal.querySelector('.sw-quick-btn-add');
  const btnBuy = modal.querySelector('.sw-quick-btn-buy');

  let currentVariantId = null;

  // Open modal on click of any quick add button
  document.querySelectorAll('.sw-card-quick-add-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const title = btn.dataset.productTitle || 'Streetwear Item';
      const price = btn.dataset.productPrice || 'Rs. 4,500';
      const variantsData = btn.dataset.productVariants;

      if (titleEl) titleEl.textContent = title;
      if (priceEl) priceEl.textContent = price;

      // Render sizes
      if (pillsContainer) {
        pillsContainer.innerHTML = '';
        let variants = [];
        try {
          variants = variantsData ? JSON.parse(variantsData) : [
            { id: '1', title: 'S' },
            { id: '2', title: 'M' },
            { id: '3', title: 'L' },
            { id: '4', title: 'XL' }
          ];
        } catch (_) {
          variants = [{ id: '1', title: 'M' }];
        }

        variants.forEach((v, idx) => {
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = `sw-size-pill ${idx === 0 ? 'selected' : ''}`;
          pill.textContent = v.title;
          pill.dataset.variantId = v.id;
          pill.addEventListener('click', () => {
            pillsContainer.querySelectorAll('.sw-size-pill').forEach(p => p.classList.remove('selected'));
            pill.classList.add('selected');
            currentVariantId = v.id;
          });
          pillsContainer.appendChild(pill);
          if (idx === 0) currentVariantId = v.id;
        });
      }

      modalBackdrop.classList.add('active');
    });
  });

  // Close modal on click outside
  modalBackdrop.addEventListener('click', (e) => {
    if (e.target === modalBackdrop) {
      modalBackdrop.classList.remove('active');
    }
  });

  const closeBtn = modal.querySelector('.sw-quick-close-btn');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => modalBackdrop.classList.remove('active'));
  }

  // Handle Add To Bag
  if (btnAdd) {
    btnAdd.addEventListener('click', async () => {
      const originalText = btnAdd.textContent;
      btnAdd.textContent = 'ADDING...';
      btnAdd.disabled = true;

      try {
        if (currentVariantId) {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: currentVariantId, quantity: 1 }] })
          });
          if (res.ok) {
            // Update cart count
            const cartRes = await fetch('/cart.js');
            const cartData = await cartRes.json();
            document.querySelectorAll('.sw-cart-count-badge').forEach(badge => {
              badge.textContent = cartData.item_count;
            });
            btnAdd.textContent = 'ADDED!';
            setTimeout(() => {
              modalBackdrop.classList.remove('active');
              btnAdd.textContent = originalText;
              btnAdd.disabled = false;
            }, 800);
            return;
          }
        }
      } catch (err) {
        console.error('Cart add error:', err);
      }

      // Fallback display
      btnAdd.textContent = 'ADDED!';
      setTimeout(() => {
        modalBackdrop.classList.remove('active');
        btnAdd.textContent = originalText;
        btnAdd.disabled = false;
      }, 700);
    });
  }

  // Handle Buy Now
  if (btnBuy) {
    btnBuy.addEventListener('click', () => {
      if (currentVariantId) {
        window.location.href = `/cart/${currentVariantId}:1`;
      } else {
        window.location.href = '/checkout';
      }
    });
  }
}

// ---------------------------------------------------------------------------
// 5. Navigation Drawer & Accordions
// ---------------------------------------------------------------------------
function initNavDrawer() {
  const backdrop = document.querySelector('.sw-drawer-backdrop');
  const openBtn = document.querySelector('.sw-header-menu-btn');
  const closeBtns = document.querySelectorAll('.sw-drawer-close-btn');

  if (openBtn && backdrop) {
    openBtn.addEventListener('click', () => backdrop.classList.add('active'));
  }

  closeBtns.forEach(btn => {
    btn.addEventListener('click', () => backdrop?.classList.remove('active'));
  });

  if (backdrop) {
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) backdrop.classList.remove('active');
    });
  }

  // Drawer Accordions
  const accordions = document.querySelectorAll('.sw-drawer-accordion-btn');
  accordions.forEach(btn => {
    btn.addEventListener('click', () => {
      const content = btn.nextElementSibling;
      const isOpen = content.style.display === 'flex';
      content.style.display = isOpen ? 'none' : 'flex';
      const icon = btn.querySelector('.sw-accordion-icon');
      if (icon) icon.textContent = isOpen ? '+' : '−';
    });
  });
}
